import { useState } from 'react';
import { FiAlertTriangle, FiX } from 'react-icons/fi';

type ConnectionType = 'shopify' | 'meta_ads';

interface DisconnectConnectionModalProps {
  isOpen: boolean;
  connectionName: string;
  connectionType: ConnectionType;
  onConfirm: () => Promise<void>;
  onCancel: () => void;
}

const CONNECTION_LABELS: Record<ConnectionType, string> = {
  shopify: 'Store',
  meta_ads: 'Meta Ads',
};

export function DisconnectConnectionModal({
  isOpen,
  connectionName,
  connectionType,
  onConfirm,
  onCancel,
}: DisconnectConnectionModalProps) {
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const label = CONNECTION_LABELS[connectionType];

  const handleConfirm = async () => {
    setIsDisconnecting(true);
    try {
      await onConfirm();
    } finally {
      setIsDisconnecting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal modal-open">
      <div className="modal-box max-w-sm">
        <button
          onClick={onCancel}
          disabled={isDisconnecting}
          className="btn btn-sm btn-circle btn-ghost absolute right-2 top-2"
        >
          <FiX className="w-4 h-4" />
        </button>

        <div className="flex justify-center mb-4">
          <div className="w-12 h-12 bg-warning/10 rounded-full flex items-center justify-center">
            <FiAlertTriangle className="w-6 h-6 text-warning" />
          </div>
        </div>

        <h3 className="font-semibold text-lg text-center">Disconnect {label}?</h3>

        <p className="py-4 text-center text-sm text-base-content/70">
          Are you sure you want to disconnect{' '}
          <span className="font-medium text-base-content">{connectionName}</span>?
        </p>

        <p className="text-center text-xs text-base-content/50 mb-6">
          This will delete all synced data. You can reconnect later.
        </p>

        <div className="modal-action justify-center gap-3">
          <button onClick={onCancel} disabled={isDisconnecting} className="btn btn-ghost">
            Cancel
          </button>
          <button onClick={handleConfirm} disabled={isDisconnecting} className="btn btn-warning">
            {isDisconnecting ? (
              <>
                <span className="loading loading-spinner loading-sm" />
                Disconnecting...
              </>
            ) : (
              'Disconnect'
            )}
          </button>
        </div>
      </div>
      <div className="modal-backdrop bg-black/40" onClick={onCancel} />
    </div>
  );
}
